import * as React from 'react';
import styled, { css } from 'styled-components';
import { ISaturationPickerProps, ISV } from './spectrum-picker-props';
import { useDrag, IConstrainedDragInfo } from '../../../hooks';
import { getOpaqueColorString, updateSV } from '../../../utils';
import { knobStyles, pickerSurfaceStyles } from './styles';

function getDragPosition(hsv: ISV) {

    const x = hsv.s;
    const y = 100 - (hsv.v);

    return {
        left: Math.round(x),
        top: Math.round(y)
    };
}

interface ISaturationSpectrumProps extends Omit<ISaturationPickerProps, 'className' | 'onValueChange' | 'color'> {
    color?: string;
    left: number;
    surfaceRef: React.RefObject<HTMLDivElement>;
    targetRef: React.RefObject<HTMLDivElement>;
    top: number;
}

const SaturationSurface = styled.div.attrs((props: Pick<ISaturationSpectrumProps, 'hueColor' | 'height' | 'width'>) => props)`
    box-sizing: border-box;
    margin-top: 12px;
    position: relative;
    display: inline-block;
    cursor: pointer;
    height: ${props => `${props.height}px`};
    width: ${props => `${props.width}px`};
    background: ${props => props.hueColor};
    ${pickerSurfaceStyles}
`;

const spectrumShadeStyle = css`
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: ${({ theme: { colorPicker: { composites: { pickerSurface } } } }) => pickerSurface.borderRadius};
`;

const SpectrumBlack = styled.div`
    ${spectrumShadeStyle}
    background: linear-gradient(to top, #000, rgba(204,154,129,0));
`;

const SpectrumWhite = styled.div`
    ${spectrumShadeStyle}
    background: linear-gradient(to right, #fff, rgba(204,154,129,0));
`;

type DragProps = Pick<ISaturationSpectrumProps, 'pointerSize' | 'left' | 'top' | 'color'>;

const Drag = styled('div').attrs(({ left, top, color }: DragProps) =>
    ({
        style: {
            left: `${left}%`,
            top: `${top}%`,
            background: ` ${color}`
        }
    })) <DragProps>`
    position: absolute;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    user-select: none;
    width: ${({ pointerSize }) => `${pointerSize}px`};
    height: ${({ pointerSize }) => `${pointerSize}px`};
    ${knobStyles}
`;

function SaturationSpectrum(props: ISaturationSpectrumProps) {
    const { color, height, hueColor, left, pointerSize, surfaceRef, targetRef, top, width } = props;
    return (
        <SaturationSurface ref={surfaceRef} hueColor={hueColor} height={height} width={width}>
            <SpectrumWhite>
                <SpectrumBlack />
            </SpectrumWhite>
            <Drag ref={targetRef} top={top} left={left} pointerSize={pointerSize} color={color} />
        </SaturationSurface>
    );
}

const SaturationPickerBase = (props: ISaturationPickerProps) => {
    const surfaceRef = React.useRef<HTMLDivElement>(null);
    const targetRef = React.useRef<HTMLDivElement>(null);
    const sv = React.useRef(props.color ? { s: props.color.s, v: props.color.v } : { s: 100, v: 100 });

    const initialDragPosition = getDragPosition(props.color);

    const handleDraging = React.useCallback((currentDragInfo: IConstrainedDragInfo) => {

        const oldHSV = sv.current;
        const saturation = Math.max(0, Math.min(100, currentDragInfo.left));
        const saturationValue = Math.max(0, Math.min(100, 100 - currentDragInfo.top));

        const newColor = updateSV({ ...props.color }, saturation, saturationValue);
        setCurrentDragInfo(currentDragInfo);
        if (oldHSV.s === newColor.s && oldHSV.v === newColor.v) {
            return;
        }
        sv.current = { s: newColor.s, v: newColor.v };
        if (props.onValueChange === undefined) {
            return;
        }

        props.onValueChange(sv.current);
    }, [props]);

    const [initialDragInfo, setDragPosition] = useDrag({
        constraintsTo: surfaceRef,
        target: targetRef,
        headDragX: initialDragPosition.left,
        headDragY: initialDragPosition.top,
        dragBufferY: (props.pointerSize! / 2),
        dragBufferX: (props.pointerSize! / 2),
        unit: '%',
        onDragging: handleDraging
    });

    const [dragInfo, setCurrentDragInfo] = React.useState<IConstrainedDragInfo>(initialDragInfo);

    React.useEffect(() => {
        const { left, top } = getDragPosition(props.color!);

        setDragPosition(left, top);
        setCurrentDragInfo({ left, top });
    }, [props.color.str, props.color.h, props.color.s, props.color.v, props.width, props.height, props.color, setDragPosition]);

    return (
        <SaturationSpectrum hueColor={props.hueColor} color={getOpaqueColorString(props.color)}
            left={dragInfo.left} top={dragInfo.top} height={props.height} pointerSize={props.pointerSize}
            width={props.width} surfaceRef={surfaceRef} targetRef={targetRef} />
    );

};

SaturationPickerBase.defaultProps = {
    height: 220,
    width: 220,
    pointerSize: 20
};
export const SaturationPicker = React.memo(SaturationPickerBase);
